"use client";

import {
  ShoppingCart,
  AlertCircle,
  HelpCircle,
  Search,
  MessageSquare,
} from "lucide-react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shadcnComponents/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shadcnComponents/ui/tabs";
import { Badge } from "@/shadcnComponents/ui/badge";
import { ICustomerQuery } from "../CampaignInterface/LiveCampaignInterface";
import { MdOpenInNew } from "react-icons/md";

export function CustomerQueryAnalysis({
  customerQueryData,
}: {
  customerQueryData: ICustomerQuery;
}) {
  const data = customerQueryData;
  const totalComments = data?.total_comments || 0;

  const hasNoQueries =
    !data?.product_query_count &&
    !data?.purchase_intent_count &&
    !data?.customer_concern_count;

  const renderMetricCard = (
    title: string,
    count: number | undefined,
    additionalInfo?: { icon: React.ReactNode; text: string }
  ) => (
    <Card
      className={`bg-gradient-to-br ${getGradientByTitle(
        title
      )} border-${getBorderColorByTitle(title)}`}
    >
      <CardHeader className="pb-2">
        <CardTitle
          className={`text-sm font-medium text-${getTextColorByTitle(title)}`}
        >
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-end justify-between">
          <div className={additionalInfo?.text ? "" : "flex flex-col"}>
            <div
              className={`text-3xl font-bold text-${getDarkTextColorByTitle(
                title
              )}`}
            >
              {count || 0}
            </div>
            {!additionalInfo?.text && title === "Purchase Intent" && (
              <div className="text-sm text-slate-500">High intent comments</div>
            )}
            {!additionalInfo?.text && title === "Customer Concerns" && (
              <div className="text-sm text-slate-500">Identified concerns</div>
            )}
          </div>
          {additionalInfo ? (
            <div className="flex items-center text-cyan-600 text-sm font-medium">
              {additionalInfo.icon}
              <span className="text-slate-500">{additionalInfo.text}</span>
            </div>
          ) : title === "Purchase Intent" ? (
            <div className="h-10 w-10 rounded-full bg-cyan-100 flex items-center justify-center">
              <ShoppingCart className="h-5 w-5 text-cyan-600" />
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );

  const getGradientByTitle = (title: string) => {
    switch (title) {
      case "Product Queries":
        return "from-indigo-50 to-cyan-50";
      case "Purchase Intent":
        return "from-cyan-50 to-blue-50";
      case "Customer Concerns":
        return "from-blue-50 to-indigo-50";
      default:
        return "from-indigo-50 to-cyan-50";
    }
  };

  const getBorderColorByTitle = (title: string) => {
    switch (title) {
      case "Product Queries":
        return "indigo-100";
      case "Purchase Intent":
        return "cyan-100";
      case "Customer Concerns":
        return "blue-100";
      default:
        return "indigo-100";
    }
  };

  const getTextColorByTitle = (title: string) => {
    switch (title) {
      case "Product Queries":
        return "indigo-700";
      case "Purchase Intent":
        return "cyan-700";
      case "Customer Concerns":
        return "blue-700";
      default:
        return "indigo-700";
    }
  };

  const getDarkTextColorByTitle = (title: string) => {
    switch (title) {
      case "Product Queries":
        return "indigo-900";
      case "Purchase Intent":
        return "cyan-900";
      case "Customer Concerns":
        return "blue-900";
      default:
        return "indigo-900";
    }
  };

  const renderQueryKeywords = (keywords) => {
    if (!keywords?.length) return null;
    const maxFrequency = Math.max(...keywords.map((k) => k.frequency || 1));

    return keywords
      .sort((a, b) => b.frequency - a.frequency)
      .map((category, index) => (
        <div key={index} className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-indigo-500"></div>
              <span className="font-medium">{category?.keyword}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {category?.frequency} queries
              </span>
              {/* <span className="text-sm font-medium">
                {(
                  (category?.frequency / data?.product_query_count) *
                  100
                )?.toFixed(2)}
                %
              </span> */}
            </div>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
            <div
              className="h-full bg-indigo-500"
              style={{
                width: `${(category?.frequency / maxFrequency) * 100}%`,
              }}
            ></div>
          </div>
        </div>
      ));
  };

  const renderQueryExamples = (queryExamples) => {
    if (!queryExamples?.length) return null;

    return queryExamples.map((queryType, index) => (
      <div key={index} className="rounded-lg border p-4 bg-white">
        <h4 className="font-medium text-indigo-800 mb-2">
          {queryType.query_category}
        </h4>
        <div className="space-y-3 mb-3">
          {queryType.examples?.slice(0, 3)?.map((example, idx) => (
            <div key={idx} className="flex items-start gap-2">
              <div className="mt-1 h-5 w-5 rounded-full bg-indigo-100 flex items-center justify-center">
                <HelpCircle className="h-3 w-3 text-indigo-600" />
              </div>
              <p className="text-sm text-slate-700 line-clamp-3">
                "{example?.comment}"
              </p>
              <span>
                <span onClick={() => window.open(example?.link, "_blank")}>
                  <MdOpenInNew className="h-4 w-4 text-slate-500 cursor-pointer" />
                </span>
              </span>
            </div>
          ))}
        </div>
        {queryType.insight && (
          <div className="text-xs text-indigo-700 bg-indigo-50 p-2 rounded">
            <span className="font-medium">Insight:</span> {queryType.insight}
          </div>
        )}
      </div>
    ));
  };

  const renderIntentKeywords = (keywords) => {
    if (!keywords?.length) return null;

    const maxFrequency = Math.max(...keywords.map((k) => k.frequency || 1));

    return keywords
      .sort((a, b) => b.frequency - a.frequency)
      .map((keyword, index) => (
        <div key={index} className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-slate-300"></div>
              <span className="font-medium">"{keyword.keyword}"</span>
            </div>
            <span className="text-sm text-muted-foreground">
              {keyword.frequency} occurrences
            </span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
            <div
              className="h-full bg-green-500"
              style={{
                width: `${(keyword?.frequency / maxFrequency) * 100}%`,
              }}
            ></div>
          </div>
        </div>
      ));
  };

  const renderHighIntentExamples = (examples) => {
    if (!examples?.length) return null;
    return examples
      .filter((item) => item?.intent_type?.toLowerCase()?.includes("high"))
      .map((example) => {
        return example?.examples?.slice(0, 3)?.map((item, index) => {
          return (
            <div key={index} className="flex items-start gap-2">
              <div className="mt-1 h-5 w-5 rounded-full bg-green-100 flex items-center justify-center">
                <ShoppingCart className="h-3 w-3 text-green-600" />
              </div>
              <p className="text-sm text-slate-700 line-clamp-3 ">
                "{item?.comment}"
              </p>
              <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                High Intent
              </Badge>
              <span>
                <span onClick={() => window.open(item?.link, "_blank")}>
                  <MdOpenInNew className="h-4 w-4 text-slate-500 cursor-pointer" />
                </span>
              </span>
            </div>
          );
        });
      });
  };
  const renderMediumIntentExamples = (examples) => {
    if (!examples?.length) return null;
    return examples
      .filter((item) => item?.intent_type?.toLowerCase()?.includes("medium"))
      .map((example) => {
        return example?.examples?.slice(0, 3)?.map((item, index) => {
          return (
            <div key={index} className="flex items-start gap-2">
              <div className="mt-1 h-5 w-5 rounded-full bg-green-100 flex items-center justify-center">
                <ShoppingCart className="h-3 w-3 text-green-600" />
              </div>
              <p className="text-sm text-slate-700 line-clamp-3 ">
                "{item?.comment}"
              </p>
              <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                Medium Intent
              </Badge>
              <span>
                <span onClick={() => window.open(item?.link, "_blank")}>
                  <MdOpenInNew className="h-4 w-4 text-slate-500 cursor-pointer" />
                </span>
              </span>
            </div>
          );
        });
      });
  };
  const renderLowIntentExamples = (examples) => {
    if (!examples?.length) return null;
    return examples
      .filter((item) => item?.intent_type?.toLowerCase()?.includes("low"))
      .map((example) => {
        return example?.examples?.slice(0, 3)?.map((item, index) => {
          return (
            <div key={index} className="flex items-start gap-2">
              <div className="mt-1 h-5 w-5 rounded-full bg-green-100 flex items-center justify-center">
                <ShoppingCart className="h-3 w-3 text-green-600" />
              </div>
              <p className="text-sm text-slate-700 line-clamp-3 ">
                "{item?.comment}"
              </p>
              <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                Low Intent
              </Badge>
              <span>
                <span onClick={() => window.open(item?.link, "_blank")}>
                  <MdOpenInNew className="h-4 w-4 text-slate-500 cursor-pointer" />
                </span>
              </span>
            </div>
          );
        });
      });
  };

  const renderConcernKeywords = (keywords) => {
    if (!keywords?.length) return null;
    const maxFrequency = Math.max(...keywords.map((k) => k.frequency || 1));

    return keywords.map((category, index) => (
      <div key={index} className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded-full bg-red-500"></div>
            <span className="font-medium">{category.keyword}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {category.frequency} mentions
            </span>
            {/* <span className="text-sm font-medium">
              {(
                (category.frequency / data?.customer_concern_count) *
                100
              )?.toFixed(2)}
              %
            </span> */}
          </div>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
          <div
            className="h-full bg-red-500"
            style={{
              width: `${(category?.frequency / maxFrequency) * 100}%`,
            }}
          ></div>
        </div>
      </div>
    ));
  };

  const renderCustomerConcerns = (concerns) => {
    if (!concerns?.length) return null;
    return concerns?.map((concern, index) => (
      <div key={index} className="rounded-lg border p-4 bg-white">
        <h4 className="font-medium text-blue-800 mb-2">
          {concern?.concern_category}
        </h4>
        <div className="space-y-3 mb-3">
          {concern?.examples?.map((example, idx) => (
            <div key={idx} className="flex items-start gap-2">
              <div className="mt-1 h-5 w-5 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="h-3 w-3 text-red-600" />
              </div>
              <p className="text-sm text-slate-700 line-clamp-3 ">
                "{example?.comment}"
              </p>
              <span>
                <span onClick={() => window.open(example?.link, "_blank")}>
                  <MdOpenInNew className="h-4 w-4 text-slate-500 cursor-pointer" />
                </span>
              </span>
            </div>
          ))}
        </div>
        {concern.recommended_action && (
          <div className="text-xs text-blue-700 bg-blue-50 p-2 rounded">
            <span className="font-medium">Recommendation:</span>{" "}
            {concern.recommended_action}
          </div>
        )}
      </div>
    ));
  };

  const renderRecommendedActions = (actions) => {
    if (!actions?.length) return null;

    return (
      <ul className="space-y-2 text-slate-700">
        {actions.map((action, index) => (
          <li key={index} className="flex items-start gap-2">
            <div className="mt-1 h-5 w-5 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600">
              {index + 1}
            </div>
            <p>{action}</p>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold tracking-tight bg-gradient-to-r from-indigo-600 to-cyan-500 bg-clip-text text-transparent">
          Customer Query Analysis
        </h2>
      </div>

      {/* Top summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {renderMetricCard("Product Queries", data?.product_query_count, {
          icon: <HelpCircle className="h-4 w-4 mr-1" />,
          text:
            data?.product_query_count === 0 || hasNoQueries
              ? "No specific queries"
              : data?.top_query_keywords?.[0]?.keyword,
        })}

        {renderMetricCard("Purchase Intent", data?.purchase_intent_count)}

        {renderMetricCard("Customer Concerns", data?.customer_concern_count, {
          icon: <AlertCircle className="h-4 w-4 mr-1" />,
          text:
            data?.customer_concern_count === 0 || hasNoQueries
              ? "No specific concern"
              : data?.top_concern_keywords?.[0]?.keyword,
        })}
      </div>

      {/* No Queries Message */}
      {hasNoQueries ||
      (data?.product_query_count === 0 && data?.purchase_intent_count === 0) ? (
        <Card className="border-indigo-100">
          <CardHeader>
            <CardTitle className="text-indigo-800">
              Customer Engagement Analysis
            </CardTitle>
            <CardDescription>
              Analysis of customer queries and concerns in comments
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 mb-6">
              <div className="h-12 w-12 rounded-full bg-indigo-50 flex items-center justify-center">
                <MessageSquare className="h-6 w-6 text-indigo-600" />
              </div>
              <p className="text-slate-700">
                {data?.product_query_count === 0 &&
                data?.purchase_intent_count === 0 &&
                data?.customer_concern_count > 0
                  ? "There are no specific product queries or purchase intent indicators."
                  : "No specific queries were identified in the comments."}
              </p>
            </div>

            {data?.customer_concern_count > 0 &&
              data?.customer_concerns?.length > 0 && (
                <div className="rounded-lg bg-amber-50 p-4 mt-4 mb-4">
                  <h3 className="text-lg font-semibold text-amber-800 mb-3">
                    Customer Concerns
                  </h3>
                  {data.customer_concerns.map((concern, index) => (
                    <div key={index} className="mb-4">
                      <h4 className="font-medium text-amber-700">
                        {concern?.concern_category}
                      </h4>
                      <div className="mt-2 space-y-2">
                        {concern.examples?.map((example, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <div className="mt-1 h-5 w-5 rounded-full bg-amber-100 flex items-center justify-center">
                              <AlertCircle className="h-3 w-3 text-amber-600" />
                            </div>
                            <p className="text-sm text-slate-700">
                              "{example?.comment}"
                            </p>
                            <span>
                              <span
                                onClick={() =>
                                  window.open(example?.link, "_blank")
                                }
                              >
                                <MdOpenInNew className="h-4 w-4 text-slate-500 cursor-pointer" />
                              </span>
                            </span>
                          </div>
                        ))}
                      </div>
                      {data?.recommended_actions?.based_on_concerns?.[
                        index
                      ] && (
                        <div className="mt-2 text-sm">
                          <span className="font-medium">Recommendation:</span>{" "}
                          {data.recommended_actions.based_on_concerns[index]}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

            {data?.recommended_actions?.based_on_concerns?.length > 0 && (
              <div className="rounded-lg bg-indigo-50 p-4 mt-4">
                <h3 className="text-lg font-semibold text-indigo-800 mb-3">
                  Recommended Actions
                </h3>
                {renderRecommendedActions(
                  data.recommended_actions.based_on_concerns
                )}
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="product-queries" className="space-y-4">
          <TabsList className="bg-slate-100 p-1">
            <TabsTrigger
              value="product-queries"
              className="data-[state=active]:bg-white data-[state=active]:text-indigo-700"
            >
              <Search className="mr-2 h-4 w-4" />
              Product Queries
            </TabsTrigger>
            <TabsTrigger
              value="purchase-intent"
              className="data-[state=active]:bg-white data-[state=active]:text-cyan-700"
            >
              <ShoppingCart className="mr-2 h-4 w-4" />
              Purchase Intent
            </TabsTrigger>
            <TabsTrigger
              value="customer-concerns"
              className="data-[state=active]:bg-white data-[state=active]:text-blue-700"
            >
              <AlertCircle className="mr-2 h-4 w-4" />
              Customer Concerns
            </TabsTrigger>
          </TabsList>

          {/* 1. PRODUCT QUERIES TAB */}
          <TabsContent value="product-queries" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-indigo-100">
                <CardHeader className="bg-gradient-to-r from-indigo-50 to-cyan-50">
                  <CardTitle className="text-indigo-800">
                    What Customers Are Asking About
                  </CardTitle>
                  <CardDescription>
                    Breakdown of product-related queries in customer comments
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="space-y-6">
                    {renderQueryKeywords(data?.top_query_keywords) || (
                      <div className="text-center text-slate-500 py-4">
                        No category data available
                      </div>
                    )}
                  </div>

                  {data?.query_insight && (
                    <div className="mt-6 rounded-lg border p-4 bg-indigo-50">
                      <h4 className="font-medium text-indigo-800 mb-2">
                        Query Insights
                      </h4>
                      <p className="text-sm text-slate-700">
                        {data.query_insight}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="border-indigo-100">
                <CardHeader className="bg-gradient-to-r from-indigo-50 to-cyan-50">
                  <CardTitle className="text-indigo-800">
                    Top Query Categories
                  </CardTitle>
                  <CardDescription>
                    Specific aspects customers are asking about
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6 space-y-6">
                  {renderQueryExamples(data?.query_examples_by_category) || (
                    <div className="text-center text-slate-500 py-4">
                      No specific query data available
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* 2. PURCHASE INTENT TAB */}
          <TabsContent value="purchase-intent" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-cyan-100">
                <CardHeader className="bg-gradient-to-r from-cyan-50 to-blue-50">
                  <CardTitle className="text-cyan-800">
                    Purchase Intent Indicators
                  </CardTitle>
                  <CardDescription>
                    Analysis of buying signals in customer comments
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="space-y-1">
                      <div className="text-sm font-medium text-cyan-800">
                        Intent Level Distribution
                      </div>
                      <div className="text-xs text-slate-500">
                        Based on {totalComments} analyzed comments
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1">
                        <div className="h-3 w-3 rounded-full bg-green-500"></div>
                        <span className="text-xs">High</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="h-3 w-3 rounded-full bg-amber-500"></div>
                        <span className="text-xs">Medium</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="h-3 w-3 rounded-full bg-slate-300"></div>
                        <span className="text-xs">Low</span>
                      </div>
                    </div>
                  </div>

                  {data?.purchase_intent_distribution && (
                    <div className="h-10 w-full bg-slate-100 rounded-full overflow-hidden mb-6">
                      <div className="flex h-full">
                        <div
                          className="h-full bg-green-500"
                          style={{
                            width: `${
                              ((data.purchase_intent_distribution.high || 0) /
                                totalComments) *
                              100
                            }%`,
                          }}
                        ></div>
                        <div
                          className="h-full bg-amber-500"
                          style={{
                            width: `${
                              ((data.purchase_intent_distribution.medium || 0) /
                                totalComments) *
                              100
                            }%`,
                          }}
                        ></div>
                        <div
                          className="h-full bg-slate-300"
                          style={{
                            width: `${
                              ((data.purchase_intent_distribution.low || 0) /
                                totalComments) *
                              100
                            }%`,
                          }}
                        ></div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-cyan-800">
                      Intent Keywords
                    </h4>
                    {renderIntentKeywords(data?.purchase_intent_keywords) || (
                      <div className="text-center text-slate-500 py-4">
                        No intent keyword data available
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="border-cyan-100">
                <CardHeader className="bg-gradient-to-r from-cyan-50 to-blue-50">
                  <CardTitle className="text-cyan-800">
                    High Intent Examples
                  </CardTitle>
                  <CardDescription>
                    Examples of comments indicating a strong purchase intent
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6 space-y-6">
                  {renderHighIntentExamples(
                    data?.intent_examples_by_category
                  ) || (
                    <div className="text-center text-slate-500 py-4">
                      No high intent examples available
                    </div>
                  )}
                  {renderMediumIntentExamples(
                    data?.intent_examples_by_category
                  )}
                  {renderLowIntentExamples(data?.intent_examples_by_category)}

                  {data?.purchase_intent_insight && (
                    <div className="mt-6 rounded-lg border p-4 bg-cyan-50">
                      <h4 className="font-medium text-cyan-800 mb-2">
                        Purchase Intent Insight
                      </h4>
                      <p className="text-sm text-slate-700">
                        {data.purchase_intent_insight}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* 3. CUSTOMER CONCERNS TAB */}
          <TabsContent value="customer-concerns" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-blue-100">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50">
                  <CardTitle className="text-blue-800">
                    Identified Customer Concerns
                  </CardTitle>
                  <CardDescription>
                    Breakdown of concerns raised in customer comments
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="space-y-6">
                    {renderConcernKeywords(data?.top_concern_keywords) || (
                      <div className="text-center text-slate-500 py-4">
                        No concern category data available
                      </div>
                    )}
                  </div>

                  {data?.concern_insight && (
                    <div className="mt-6 rounded-lg border p-4 bg-blue-50">
                      <h4 className="font-medium text-blue-800 mb-2">
                        Concern Insights
                      </h4>
                      <p className="text-sm text-slate-700">
                        {data.concern_insight}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="border-blue-100">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50">
                  <CardTitle className="text-blue-800">
                    Specific Concerns & Recommendations
                  </CardTitle>
                  <CardDescription>
                    Detailed concerns and suggested actions
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6 space-y-6">
                  {renderCustomerConcerns(data?.customer_concerns) || (
                    <div className="text-center text-slate-500 py-4">
                      No specific concern data available
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
